import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  useTheme,
  Dialog,
  DialogTitle,
  Button,
  DialogContentText,
} from "@mui/material";
import {
  useEffect,
  useState,
  useContext,
  Dispatch,
  SetStateAction,
  memo,
  useMemo,
  useCallback,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from "react";
import axios, { AxiosResponse } from "axios";
import dayjs from "dayjs";
//src
import Check from "../../../../images/icons/white_edit/Check_white.svg";
import MOCK_IMAGE from "../../../../images/mock_images/pointcloud_img2.png";
import AR_IMAGE from "../../../../images/mock_images/ar_img.png";
import { config } from "../../../config/config";
import {
  deleteContext,
  EViewType,
  renameDialogContext,
  deleteDialogContext,
} from "../../../pages/Home";
import { useInView } from "react-intersection-observer";
import { MoreVert } from "@mui/icons-material";
import { Common } from "../../../../common/src";
import DownloadingIcon from "@mui/icons-material/Downloading";
import { t } from "i18next";
import POINT_REMOVE_WHITE_IMG from "../../../../images/icons/white_edit/point_remove.svg";
import POINT_REMOVE_IMG from "../../../../images/icons/point_remove.svg";
import ChevronRight from "../../../../images/icons/neutral_edit/Chevron Right.svg";
import Icon from "../../Icon";
import { projectDetailAPIs } from "../../../api/project";
import { uploadDialogContext } from "../../../pages/Home";
import Clusterings from "../../Utility/Clusterings";
import { openProjectAfterCheck } from "./OpenProjectTab";
import { useNavigate } from "react-router-dom";
import { StatusText } from "./ProjectStatusText";
import {
  ColumnVisibility,
  getColumnSizes,
  THUMBNAIL_WIDTH_PX,
  ACTIONS_WIDTH_PX,
} from "./projectListColumnWidths";

type StatusIconProps = {
  status: 0 | 1 | 2; // 0:default/1:success/2:error
  number: number;
};

//new status text
const StatusIcon = (props: StatusIconProps) => {
  return (
    <Box component={"div"} sx={{ display: "inline-block" }}>
      {props.status === 1 ? (
        <Box
          component={"div"}
          className={
            props.number === 5
              ? "pj-status-dashboard comm-status"
              : "pj-status comm-status"
          }
        >
          <img src={Check} alt="" />
        </Box>
      ) : props.status === 2 ? (
        <Box
          component={"div"}
          className="pj-status1 comm-status"
          sx={{ color: "#FFFFFF" }}
        >
          {props.number}
        </Box>
      ) : (
        <Box
          component={"div"}
          className="pj-status2 comm-status"
          sx={{ color: "#FFFFFF" }}
        >
          {props.number}
        </Box>
      )}
    </Box>
  );
};

type Props = {
  project: Common.Project;
  selectProject: Common.Project[];
  setSelectProject: (project: Common.Project[]) => void;
  jobsiteNameList: Common.LandlogJobSite[];
  userNameList: Common.ProjectUserList[];
  isLanguage: boolean;
  setErrorSnackbar: Dispatch<SetStateAction<boolean>>;
  setErrorMsg: Dispatch<SetStateAction<string>>;
  setOpenWarning: Dispatch<SetStateAction<boolean>>;
  setMsgWarning: Dispatch<SetStateAction<string>>;
  isARAvailable: boolean;
  viewType: EViewType;
  sortBy?: string;
  visibleColumns: ColumnVisibility;
};

const Project = (props: Props) => {
  // console.log("project");

  // Memoize filtered jobsite and user to avoid array.filter() on every render
  const { jobsiteName: memoizedJobsiteName, userName: memoizedUserName } =
    useMemo(() => {
      const jobsite =
        props.jobsiteNameList?.find(
          (data) => data.id === props.project.jobSiteId,
        )?.name || "";
      const user =
        props.userNameList?.find((data) => data.userId === props.project.userId)
          ?.name || "";
      return { jobsiteName: jobsite, userName: user };
    }, [
      props.jobsiteNameList,
      props.userNameList,
      props.project.jobSiteId,
      props.project.userId,
    ]);

  let isGridView = props.viewType === EViewType.GRID;
  // ArMarker処理
  const [uploadedFailed, setUploadedFailed] = useState<boolean>(false);
  const [uploadedComp, setUploadedComp] = useState<boolean>(false);
  // shm処理
  const [sfmComp, setSfmComp] = useState<boolean>(false);
  const [sfmFailed, setSfmFailed] = useState<boolean>(false);
  // helmart処理
  const [helmertComp, sethelmertComp] = useState<boolean>(false);
  const [helmertFailed, setHelmertFailed] = useState<boolean>(false);
  // verification処理
  const [verificationComp, setVerificationComp] = useState<boolean>(false);

  const [dashboardUploadComp, setDashboardUploadComp] =
    useState<boolean>(false);

  //削除、プロジェクト名変更ボタン関連
  const [anchorEl, setAnchorEl] = useState(null);

  const [thumbnail, setThumbnail] = useState<string>();
  const jobsiteName = memoizedJobsiteName;
  const userName = memoizedUserName;
  const [projectName, setProjectName] = useState<string>();

  /**
   * CSS only creates line-break opportunities at spaces and "-"; "_" is an
   * alphabetic character (UAX #14), so "AAA_BBB_CCC" counts as ONE word: it is
   * pushed onto a new line as a whole and only then broken mid-word, leaving a
   * gap at the end of the previous line.
   * Insert <wbr /> after every "_" to add a valid break opportunity, keeping the
   * "_" at the end of the line just like "-" behaves. <wbr /> is used instead of
   * a zero-width space character so no stray character leaks into the text when
   * the user copies the project name.
   */
  const projectNameNodes = useMemo<ReactNode[]>(() => {
    const segments = (projectName ?? "").split("_");
    const nodes: ReactNode[] = [];
    segments.forEach((segment, index) => {
      const isLast = index === segments.length - 1;
      nodes.push(isLast ? segment : `${segment}_`);
      if (!isLast) nodes.push(<wbr key={index} />);
    });
    return nodes;
  }, [projectName]);

  const formatDateParts = useCallback(
    (date?: Date) => {
      if (!date) return { dateText: "-", timeText: "" };
      const d = dayjs(date);
      return {
        dateText: d.format(props.isLanguage ? "YYYY/MM/DD" : "DD/MM/YYYY"),
        timeText: d.format("HH:mm:ss"),
      };
    },
    [props.isLanguage],
  );

  const uploadDateParts = useMemo(
    () => formatDateParts(props.project.uploadTime),
    [props.project.uploadTime, formatDateParts],
  );
  const captureDateParts = useMemo(
    () => formatDateParts(props.project.captureTime),
    [props.project.captureTime, formatDateParts],
  );

  const columnSizes = useMemo(
    () => getColumnSizes(props.visibleColumns),
    [props.visibleColumns],
  );

  const [jobsiteId, setJobSiteId] = useState<string>();

  const [isAR, setIsAR] = useState<boolean>(false);
  const [openARListDialog, setOpenARListDialog] = useState<boolean>(false);

  const [messageModal, setMessageModal] = useState<string>("");

  const getThumbnail = async (controller: AbortController) => {
    try {
      axios
        .get(
          `${config.serverHost}/api/v1/project/${props.project.id}/thumbnail`,
          {
            withCredentials: true,
            signal: controller.signal,
          },
        )
        .then(async (res: AxiosResponse<string>) => {
          axios
            .get(`${res.data}`, {
              responseType: "blob",
              withCredentials: true,
              signal: controller.signal,
            })
            .then(async (res: AxiosResponse<Blob>) => {
              setThumbnail(URL.createObjectURL(res.data));
            });
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            // console.log("thumbnail request cancelled");
          } else {
            console.error(`thumbnail get err ${err}`);
          }
        });
    } catch (err) {
      console.error(`thumbnail get err ${err}`);
    }
  };

  const isUploadCompletedCheck = async () => {
    try {
      const res: AxiosResponse<Common.ProjectViDoc> =
        await projectDetailAPIs.uploadStatus.get(props.project.id);

      return res.status === 200; //  process COMPLETED
    } catch (err) {
      console.error(`thumbnail get err ${err}`);
    }
  };

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  const memoizedGetThumbnail = useCallback(
    async (controller: AbortController) => {
      if (thumbnail) return;
      await getThumbnail(controller);
    },
    [thumbnail],
  );

  useEffect(() => {
    if (!inView) return;
    const controller = new AbortController();
    memoizedGetThumbnail(controller);
    return () => {
      controller.abort();
    };
  }, [inView, memoizedGetThumbnail]);

  const onDismissMessageModal = () => {
    setMessageModal("");
  };

  useEffect(() => {
    setProjectName(props.project.name);
  }, [props.project.name]);

  useEffect(() => {
    setProjectName(props.project.name);
    if (props.project.status) {
      setIsAR(props.project.status.is_ar);
      if (
        props.project.status.lidar_upload ===
          Common.PROJECT_STATUS_ENUM.COMPLETED ||
        props.project.status.lidar_upload ===
          Common.PROJECT_STATUS_ENUM.WAITING ||
        props.project.status.sfm_processing ===
          Common.PROJECT_STATUS_ENUM.PROCESSING ||
        props.project.status.sfm_processing ===
          Common.PROJECT_STATUS_ENUM.WAITING
      ) {
        setUploadedComp(true);
      }
      if (
        props.project.status.lidar_upload === Common.PROJECT_STATUS_ENUM.ERROR
      ) {
        setUploadedFailed(true);
      }
      if (
        props.project.status.sfm_processing ===
        Common.PROJECT_STATUS_ENUM.COMPLETED
      ) {
        setSfmComp(true);
      }
      if (
        props.project.status.sfm_processing === Common.PROJECT_STATUS_ENUM.ERROR
      ) {
        setSfmFailed(true);
      }
      if (
        props.project.status.helmert_transformation ===
          Common.PROJECT_STATUS_ENUM.COMPLETED ||
        props.project.status.coordinate_transformation ===
          Common.PROJECT_STATUS_ENUM.COMPLETED
      ) {
        sethelmertComp(true);
      }
      if (
        props.project.status.helmert_transformation ===
          Common.PROJECT_STATUS_ENUM.ERROR ||
        props.project.status.coordinate_transformation ===
          Common.PROJECT_STATUS_ENUM.ERROR
      ) {
        setHelmertFailed(true);
      }
      if (
        props.project.status.verification_helmert ===
          Common.PROJECT_STATUS_ENUM.COMPLETED ||
        props.project.status.verification_sfm ===
          Common.PROJECT_STATUS_ENUM.COMPLETED
      ) {
        setVerificationComp(true);
      }
      if (
        props.project.status.dashboard_upload_las_helmert ===
          Common.PROJECT_STATUS_ENUM.COMPLETED ||
        props.project.status.dashboard_upload_las_sfm ===
          Common.PROJECT_STATUS_ENUM.COMPLETED ||
        props.project.status.dashboard_upload_txt_helmert ===
          Common.PROJECT_STATUS_ENUM.COMPLETED ||
        props.project.status.dashboard_upload_txt_sfm ===
          Common.PROJECT_STATUS_ENUM.COMPLETED ||
        props.project.status.landlog_viewer_upload_helmert ===
          Common.PROJECT_STATUS_ENUM.COMPLETED ||
        props.project.status.landlog_viewer_upload_sfm ===
          Common.PROJECT_STATUS_ENUM.COMPLETED
      ) {
        setDashboardUploadComp(true);
      }
    }
  }, []);

  const { openUploadDialog } = useContext(uploadDialogContext);
  const { openRenameDialog } = useContext(renameDialogContext);
  const { openDeleteDialog } = useContext(deleteDialogContext);
  const navigate = useNavigate();
  //プロジェクト削除リスト
  const [selectList, setSelectList] = useState(false);
  //プロジェクト削除有効化フラグ
  const { isDelete } = useContext(deleteContext);
  useEffect(() => {
    if (isDelete === false) {
      setSelectList(false);
      props.selectProject.length = 0;
    }
  });

  const handleList = () => {
    !selectList
      ? props.setSelectProject([...props.selectProject, props.project])
      : props.setSelectProject(
          props.selectProject.filter((arr) => arr.id !== props.project.id),
        );
    setSelectList((prev) => !prev);
  };

  const handleThumbnailClick = (event: ReactMouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (isDelete) {
      handleList();
      return;
    }
    openProjectAfterCheck(props.project.id, isAR, {
      isReady: async () => (await isUploadCompletedCheck()) === true,
      onNotReady: () => setMessageModal(t("PROJECT_UPLOAD_PROCESSING")),
      origin: window.location.origin,
      openWindow: (url, target) => window.open(url, target),
    });
  };

  const handleClick = (event: any) => {
    // カード全体のクリック（別タブ起動）に伝播させない。
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (e) => {
    setAnchorEl(null);
    e.stopPropagation();
  };

  const handleRowClick = async () => {
    if (isDelete) {
      handleList();
      return;
    }
    let ready = await isUploadCompletedCheck();
    if (!ready) {
      setMessageModal(t("PROJECT_UPLOAD_PROCESSING"));
      return;
    }
    sessionStorage.setItem("search", window.location.search);
    if (isAR) navigate(`ar/${props.project.id}`);
    else navigate(`/${props.project.id}`);
  };

  const handleDeleteDialogOpen = () => {
    setAnchorEl(null);
  };

  const handleRenameDialogOpen = () => {
    setAnchorEl(null);
  };

  // List View only: the ⋮ menu renders through a MUI Portal, so a click on a
  // menu item still bubbles up to the row Card's onClick (navigate) unless
  // each entry point stops it here.
  const withStopPropagation =
    (handler: () => void) => (e: React.MouseEvent) => {
      e.stopPropagation();
      handler();
    };

  const isEditing = () => {
    const status = props.project.status;
    const EDITING = Common.PROJECT_STATUS_ENUM.PROCESSING;
    return (
      status !== null &&
      (status.helmert_transformation === EDITING ||
        status.coordinate_transformation === EDITING ||
        status.point_splitting_lidar === EDITING ||
        status.remove_lidar === EDITING ||
        status.point_txt_lidar === EDITING ||
        status.point_splitting_sfm === EDITING ||
        status.remove_sfm === EDITING ||
        status.point_txt_sfm === EDITING ||
        status.point_splitting_helmert === EDITING ||
        status.remove_helmert === EDITING ||
        status.point_txt_helmert === EDITING)
    );
  };

  const theme = useTheme();
  const DARK = "dark";
  const removeIcon =
    theme.palette.mode === DARK ? POINT_REMOVE_WHITE_IMG : POINT_REMOVE_IMG;

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const customMenuColor = {
    color: theme.palette.base.menu,
  };

  const selectedBorderColor =
    theme.palette.mode === DARK ? "#FFFFFF" : "#000000";

  const customBackgroundStyles = {
    background: theme.palette.base.modal,
  };

  const customDialogBackgroundColor = {
    color: theme.palette.base.dialogBackground,
  };

  const customMainColor = {
    background: theme.palette.primary.main,
    boxShadow: "none",
  };

  const RightIcon = () => {
    return (
      <div
        style={{
          height: "24px",
          width: "24px",
          right: "6px",
          position: "absolute",
        }}
      >
        <Icon imagePath={ChevronRight} color={customColor.color} />
      </div>
    );
  };

  const getProjectItemStyle = () => {
    if (isGridView) {
      return {
        width: "220px",
        height: "290px",
      };
    } else {
      return {
        width: "100%",
      };
    }
  };

  return (
    <Grid
      item
      key={props.project.id}
      ref={ref}
      sx={{
        margin: isGridView ? "6.5px" : "0px",
        padding: "0px",
        ...getProjectItemStyle(),
      }}
    >
      <Card
        onClick={isGridView ? handleList : undefined}
        sx={{
          borderColor: selectedBorderColor,
          margin: "0px",
          flexDirection: isGridView ? "column" : "row",
          border: isGridView ? (selectList ? "solid 2px" : "0px") : "0px",
          boxShadow: isGridView
            ? "0 2px 8px rgba(0, 0, 0, 0.18)"
            : "none !important",
          ...getProjectItemStyle(),
        }}
      >
        {inView ? (
          isGridView ? (
            <Box component={"div"}>
              <Box
                component={"div"}
                sx={{
                  width: "100%",
                  height: "32px",
                  verticalAlign: "middle",
                  margin: "0px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="h5"
                  component="h5"
                  sx={{
                    height: "100%",
                    width: "100%",
                    paddingLeft: "8px",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    verticalAlign: "middle",
                  }}
                >
                  <Tooltip
                    title={projectName}
                    arrow
                    placement="top"
                    disableInteractive
                  >
                    <Box
                      component={"div"}
                      className="pj-tooltip"
                      sx={{ display: "inline-block", maxWidth: "100%" }}
                    >
                      {projectName}
                    </Box>
                  </Tooltip>
                </Typography>
                <IconButton
                  sx={{
                    display: "flex",
                    width: "30px",
                    height: "30px",
                    outline: "none",
                  }}
                  onClick={handleClick}
                >
                  <MoreVert fontSize="medium" sx={customColor} />
                </IconButton>
                <Menu
                  id="simple-menu"
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  sx={{ top: "10px", borderRadius: "0px", left: "-132px" }}
                  PaperProps={{
                    style: {
                      backgroundColor: customMenuColor.color,
                      textAlign: "center",
                      borderRadius: "0px",
                    },
                  }}
                >
                  <MenuItem
                    onClick={handleRenameDialogOpen}
                    sx={{ width: "230px", padding: "5px", ...customColor }}
                  >
                    <div
                      onClick={() => {
                        openRenameDialog(props.project);
                        setAnchorEl(null);
                      }}
                      style={{
                        width: "100%",
                        height: "100%",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      {t("CHANGE_TITLE")}
                      <RightIcon />
                    </div>
                  </MenuItem>
                  <MenuItem
                    onClick={handleDeleteDialogOpen}
                    sx={{ width: "230px", padding: "5px", ...customColor }}
                  >
                    <div
                      onClick={() => {
                        openDeleteDialog(props.project);
                        setAnchorEl(null);
                      }}
                      style={{
                        width: "100%",
                        height: "100%",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      {t("REMOVE")}
                      <RightIcon />
                    </div>
                  </MenuItem>

                  {props.isARAvailable && (
                    <MenuItem
                      onClick={() => {
                        openUploadDialog(props.project);
                        setAnchorEl(null);
                      }}
                      sx={{ width: "230px", padding: "5px", ...customColor }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        {t("UPLOAD")}
                        <RightIcon />
                      </div>
                    </MenuItem>
                  )}
                </Menu>
              </Box>
              <Box
                component={"div"}
                onClick={handleThumbnailClick}
                sx={{ position: "relative", cursor: "pointer" }}
              >
                <CardMedia
                  className="hover-cursor"
                  component="img"
                  height="100px"
                  width="220px"
                  image={
                    !isAR ? (thumbnail ? thumbnail : MOCK_IMAGE) : AR_IMAGE
                  }
                />
                <Avatar
                  sx={{
                    backgroundColor: props.project?.useViDoc
                      ? "#2E7D32"
                      : "#4C6CB3",
                    color: "white",
                    position: "absolute",
                    bottom: "0px",
                    right: "0px",
                    height: "24px",
                    width: "44px",
                    fontSize: "small",
                  }}
                  variant="square"
                >
                  <Typography
                    gutterBottom
                    variant="body2"
                    component="div"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      height: "20px",
                      margin: "0px",
                    }}
                  >
                    {props.project?.useViDoc ? "RTK" : "GPS"}
                  </Typography>
                </Avatar>
              </Box>
              <CardContent
                sx={{
                  padding: "8px !important",
                  display: "flex",
                  flexFlow: "column",
                  height: "158px",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="body2"
                  component="div"
                  sx={{ padding: "0px" }}
                >
                  {props.isLanguage
                    ? dayjs(
                        Clusterings.resolveSortDate(
                          props.project,
                          props.sortBy,
                        ),
                      ).format("YYYY/MM/DD HH:mm:ss")
                    : dayjs(
                        Clusterings.resolveSortDate(
                          props.project,
                          props.sortBy,
                        ),
                      ).format("DD/MM/YYYY HH:mm:ss")}
                </Typography>
                <Box
                  component={"div"}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Tooltip
                    title={jobsiteName}
                    arrow
                    placement="top"
                    disableInteractive
                  >
                    <Typography
                      gutterBottom
                      variant="body2"
                      component="div"
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        height: "20px",
                        margin: "0px",
                      }}
                    >
                      {jobsiteName}
                    </Typography>
                  </Tooltip>
                  <Box component={"div"}>
                    {(props.project.status.remove_helmert ===
                      Common.PROJECT_STATUS_ENUM.COMPLETED ||
                      props.project.status.remove_sfm ===
                        Common.PROJECT_STATUS_ENUM.COMPLETED ||
                      props.project.status.remove_lidar ===
                        Common.PROJECT_STATUS_ENUM.COMPLETED) && (
                      <Tooltip
                        title={
                          <span className="tool-tip-cal">
                            {t("REMOVED_POINT_CLOUD")}
                          </span>
                        }
                        arrow
                        placement="top"
                        disableInteractive
                      >
                        <img src={removeIcon} alt="" />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Typography
                  gutterBottom
                  variant="body2"
                  component="div"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    height: "20px",
                    margin: "0px",
                  }}
                >
                  {userName}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box
                    component={"div"}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <StatusIcon
                      status={uploadedComp ? 1 : uploadedFailed ? 2 : 0}
                      number={1}
                    />
                    <span style={{ margin: "0 3px" }}></span>
                    <StatusIcon
                      status={sfmComp ? 1 : sfmFailed ? 2 : 0}
                      number={2}
                    />
                    <span style={{ margin: "0 3px" }}></span>
                    <StatusIcon
                      status={helmertComp ? 1 : helmertFailed ? 2 : 0}
                      number={3}
                    />
                    <span style={{ margin: "0 3px" }}></span>
                    <StatusIcon status={verificationComp ? 1 : 0} number={4} />
                    <span style={{ margin: "0 3px" }}></span>
                    <StatusIcon
                      status={dashboardUploadComp ? 1 : 0}
                      number={5}
                    />
                  </Box>
                  {isEditing() && (
                    <Tooltip
                      title={
                        <span className="tool-tip-cal">{t("WHILE_EDIT")}</span>
                      }
                      arrow
                      placement="top"
                      disableInteractive
                    >
                      <DownloadingIcon
                        sx={{
                          color:
                            theme.palette.mode === DARK ? "white" : "#140A94",
                          width: "24px",
                          height: "24px",
                          transform: "rotate(180deg)",
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
                <StatusText
                  status={props.project.status}
                  isLanguage={props.isLanguage}
                />
              </CardContent>
            </Box>
          ) : (
            <Box component={"div"} sx={{ position: "relative", width: "100%" }}>
              <Card
                key={props.project.id}
                sx={{
                  borderColor: selectedBorderColor,
                  border: selectList
                    ? `2px solid ${selectedBorderColor}`
                    : "1px solid transparent",
                  width: "100%",
                  boxShadow: selectList
                    ? "0 1px 4px rgba(25, 118, 210, 0.12)"
                    : "0 1px 3px rgba(0, 0, 0, 0.06)",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  margin: "4px 0",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  {/* Thumbnail — fixed width, outside the 24-unit column system */}
                  <Box
                    sx={{
                      flex: `0 0 ${THUMBNAIL_WIDTH_PX}px`,
                      cursor: "pointer",
                    }}
                    component={"div"}
                    onClick={handleThumbnailClick}
                  >
                    <CardMedia
                      className="hover-cursor"
                      component="img"
                      style={{
                        height: "100px",
                        width: "100%",
                        maxWidth: "220px",
                        borderRadius: "6px",
                        objectFit: "cover",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                      }}
                      image={
                        !isAR ? (thumbnail ? thumbnail : MOCK_IMAGE) : AR_IMAGE
                      }
                    />
                  </Box>
                  <Grid
                    container
                    columns={columnSizes.total}
                    alignItems={"center"}
                    spacing={0}
                    sx={{ flex: 1 }}
                  >
                    {/* Project Name */}
                    {props.visibleColumns.showName && (
                      <Grid item xs={columnSizes.name}>
                        <Typography
                          sx={{
                            paddingLeft: "10px",
                            textAlign: "left",
                            fontWeight: 500,
                            fontSize: "15px",
                            lineHeight: 1.4,
                          }}
                        >
                          <Tooltip
                            title={projectName}
                            arrow
                            placement="top"
                            disableInteractive
                          >
                            <Box
                              component={"div"}
                              sx={{
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 4,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                // Shrink to the rendered text width so the
                                // tooltip anchors right above the name instead
                                // of centering over the whole (often much
                                // wider) left-aligned grid cell.
                                width: "fit-content",
                                maxWidth: "100%",
                              }}
                            >
                              {projectNameNodes}
                            </Box>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    )}
                    {/* Jobsite */}
                    {props.visibleColumns.showJobsite && (
                      <Grid item xs={columnSizes.jobsite}>
                        <Tooltip
                          title={jobsiteName}
                          arrow
                          placement="top"
                          disableInteractive
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              padding: "0 8px",
                              textAlign: "center",
                              color: "text.secondary",
                            }}
                          >
                            {jobsiteName}
                          </Typography>
                        </Tooltip>
                      </Grid>
                    )}
                    {/* Photographer */}
                    {props.visibleColumns.showPhotographer && (
                      <Grid item xs={columnSizes.photographer}>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            padding: "0 8px",
                            textAlign: "center",
                            color: "text.secondary",
                          }}
                        >
                          {userName}
                        </Typography>
                      </Grid>
                    )}
                    {/* Type */}
                    {props.visibleColumns.showType && (
                      <Grid item xs={columnSizes.type}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Avatar
                            sx={{
                              backgroundColor: props.project?.useViDoc
                                ? "#2E7D32"
                                : "#4C6CB3",
                              color: "white",
                              height: "28px",
                              width: "52px",
                              fontSize: "12px",
                              fontWeight: 600,
                              borderRadius: "14px",
                            }}
                            variant="rounded"
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                fontSize: "11px",
                              }}
                            >
                              {props.project?.useViDoc ? "RTK" : "GPS"}
                            </Typography>
                          </Avatar>
                        </Box>
                      </Grid>
                    )}
                    {/* Capture date */}
                    {props.visibleColumns.showCaptureDate && (
                      <Grid item xs={columnSizes.captureDate}>
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{
                            padding: "0 2px",
                            textAlign: "center",
                            fontSize: "13px",
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                            lineHeight: 1.35,
                          }}
                        >
                          <Box component="span" sx={{ display: "block" }}>
                            {captureDateParts.dateText}
                          </Box>
                          <Box component="span" sx={{ display: "block" }}>
                            {captureDateParts.timeText}
                          </Box>
                        </Typography>
                      </Grid>
                    )}
                    {/* Upload date */}
                    {props.visibleColumns.showUploadDate && (
                      <Grid item xs={columnSizes.uploadDate}>
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{
                            padding: "0 2px",
                            textAlign: "center",
                            fontSize: "13px",
                            color: "text.secondary",
                            // nowrap applies per line: keeps the date and the time
                            // each intact instead of breaking inside "DD/MM/YYYY".
                            whiteSpace: "nowrap",
                            lineHeight: 1.35,
                          }}
                        >
                          <Box component="span" sx={{ display: "block" }}>
                            {uploadDateParts.dateText}
                          </Box>
                          <Box component="span" sx={{ display: "block" }}>
                            {uploadDateParts.timeText}
                          </Box>
                        </Typography>
                      </Grid>
                    )}
                    {/* Images (was always-shown "number of images") */}
                    {props.visibleColumns.showImages && (
                      <Grid item xs={columnSizes.images}>
                        <Typography
                          variant="body2"
                          sx={{
                            padding: "0 8px",
                            textAlign: "center",
                            fontSize: "13px",
                            color: "text.secondary",
                          }}
                        >
                          {props.project.numberOfImage ?? "-"}
                        </Typography>
                      </Grid>
                    )}
                    {/* Status */}
                    {props.visibleColumns.showStatus && (
                      <Grid item xs={columnSizes.status}>
                        <Box sx={{ textAlign: "left" }}>
                          <StatusText
                            status={props.project.status}
                            isLanguage={props.isLanguage}
                            wrapTwoLines
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                  {/* Actions — fixed width, outside the 24-unit column system */}
                  <Box sx={{ flex: `0 0 ${ACTIONS_WIDTH_PX}px` }}>
                    <IconButton
                      disabled={isDelete}
                      sx={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClick(e);
                      }}
                    >
                      <MoreVert fontSize="medium" sx={customColor} />
                    </IconButton>
                    <Menu
                      id="simple-menu"
                      anchorEl={anchorEl}
                      keepMounted
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                      sx={{ top: "0px", borderRadius: "0px", left: "-40px" }}
                      PaperProps={{
                        style: {
                          backgroundColor: customMenuColor.color,
                          textAlign: "center",
                          borderRadius: "0px",
                        },
                      }}
                    >
                      <MenuItem
                        onClick={withStopPropagation(handleRenameDialogOpen)}
                        sx={{ width: "230px", padding: "5px", ...customColor }}
                      >
                        <div
                          onClick={() => {
                            openRenameDialog(props.project);
                            setAnchorEl(null);
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          {t("CHANGE_TITLE")}
                          <RightIcon />
                        </div>
                      </MenuItem>
                      <MenuItem
                        onClick={withStopPropagation(handleDeleteDialogOpen)}
                        sx={{ width: "230px", padding: "5px", ...customColor }}
                      >
                        <div
                          onClick={() => {
                            openDeleteDialog(props.project);
                            setAnchorEl(null);
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          {t("REMOVE")}
                          <RightIcon />
                        </div>
                      </MenuItem>
                      {props.isARAvailable && (
                        <MenuItem
                          onClick={withStopPropagation(() => {
                            openUploadDialog(props.project);
                            setAnchorEl(null);
                          })}
                          sx={{
                            width: "230px",
                            padding: "5px",
                            ...customColor,
                          }}
                        >
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            {t("UPLOAD")}
                            <RightIcon />
                          </div>
                        </MenuItem>
                      )}
                    </Menu>
                  </Box>
                </Box>
              </Card>
            </Box>
          )
        ) : (
          <></>
        )}
        {/* アップロード完了ダイアログ
            Rendered outside the isGridView branches so both the Grid view
            (thumbnail click) and the List view (row click) show it. */}
        <Dialog
          open={!!messageModal && messageModal.trim() !== ""}
          PaperProps={{
            className: "message-modal-container",
            style: { ...customBackgroundStyles },
          }}
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: customDialogBackgroundColor.color,
              },
            },
          }}
        >
          <DialogContentText
            sx={{
              padding: "0 20px",
              textAlign: "center !important",
              color: theme.palette.text.primary,
              width: "100%",
            }}
            id="upload-file20"
          >
            {messageModal}
          </DialogContentText>
          <Button
            style={customMainColor}
            className="upload-file22"
            size="small"
            variant="contained"
            onClick={withStopPropagation(onDismissMessageModal)}
          >
            {"OK"}
          </Button>
        </Dialog>
      </Card>
    </Grid>
  );
};

export default memo(Project);
